/**
 * M4: one normalization idiom for the app language.
 *
 * Three spellings used to coexist: `startsWith('de')` in `plans.ts` and
 * `checkout.ts` against `=== 'de'` in `useLanguage.ts`, `UpgradeBanner.tsx` and
 * `Pricing.tsx`. `supportedLngs: ['en','de']` keeps the difference unreachable
 * today, but the moment a regional tag such as `de-AT` reaches i18next the two
 * spellings disagree — `UpgradeBanner` would print "2,000" on one line and
 * "2.000" four lines below it, inside the same sentence.
 *
 * Deliberately import-free (no i18next, no locale bundle) so `checkout.ts` can
 * use it without pulling the translation resources into the billing path.
 */

/** True for `de`, `de-AT`, `DE-CH`, … — anything i18next may report as German. */
export function isGerman(language: string | null | undefined): boolean {
  return (language || '').toLowerCase().startsWith('de');
}

/** BCP 47 tag for `toLocaleString` / `Intl` calls in the active app language. */
export function numberLocale(language: string | null | undefined): string {
  return isGerman(language) ? 'de-DE' : 'en-US';
}

/** Standalone placeholder for "no value" — never prose, so the em-dash rule does not apply. */
const NA_DASH = '—';

/**
 * Dezimalzahl in der App-Sprache (Design-Audit F18).
 *
 * `toFixed` schreibt IMMER einen Punkt. Auf der Wirkung-Seite stand dadurch
 * "729.5 %" neben "p < 0,05" und "1000" neben "3.769" — drei Schreibweisen in
 * einem Bild, auf ausgerechnet der Seite, deren einziges Versprechen
 * Messgenauigkeit ist. `minimumFractionDigits` deckt sich mit `toFixed`:
 * nachgestellte Nullen bleiben stehen, damit eine Spalte gleich breit bleibt.
 */
export function formatDecimal(
  value: number | null | undefined,
  language: string | null | undefined,
  digits = 1,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NA_DASH;
  return value.toLocaleString(numberLocale(language), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Ganze Zahl mit Tausendertrenner der App-Sprache. */
export function formatInteger(
  value: number | null | undefined,
  language: string | null | undefined,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NA_DASH;
  return Math.round(value).toLocaleString(numberLocale(language));
}

/** Vorzeichenbehaftete Prozentzahl, z.B. "+12,4 %" / "+12.4%". */
export function formatSignedPercent(
  value: number | null | undefined,
  language: string | null | undefined,
  digits = 1,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NA_DASH;
  const sign = value >= 0 ? '+' : '';
  return `${sign}${formatDecimal(value, language, digits)}%`;
}
